import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock, Tag, Share, ChevronLeft, ChevronRight } from "lucide-react";
import { blogPosts, BlogPost } from "@/data/blog-posts";

export const BlogDetails = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading
    setIsLoading(true);
    
    // Find the post with the matching slug
    const foundPost = blogPosts.find((p) => p.slug === slug);
    
    if (foundPost) {
      setPost(foundPost);
      
      // Find related posts (same category, excluding current post)
      const related = blogPosts
        .filter((p) => p.category === foundPost.category && p.id !== foundPost.id)
        .slice(0, 3);
      setRelatedPosts(related);
    }
    
    // Simulate API delay
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, [slug]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Handle share functionality
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      }).catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback for browsers that don't support navigator.share
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Find post index for navigation
  const currentIndex = post ? blogPosts.findIndex(p => p.id === post.id) : -1;
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-muted-foreground mt-4">Loading article...</p>
      </div>
    );
  }

  // Show not found state
  if (!post) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-3xl font-bold mb-4">Article Not Found</h1>
        <p className="text-muted-foreground mb-8">The article you're looking for doesn't exist or has been removed.</p>
        <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Back button */}
      <div className="mb-8">
        <Button variant="outline" size="sm" onClick={() => navigate('/blog')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Blog
        </Button>
      </div>

      {/* Article header */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="inline-flex items-center text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
            {post.category}
          </span>
          {post.tags.map((tag) => (
            <span 
              key={tag} 
              className="inline-flex items-center text-sm bg-muted px-3 py-1 rounded-full"
            >
              <Tag className="h-3 w-3 mr-1" />
              {tag}
            </span>
          ))}
        </div>
        
        <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <img 
                src={post.author.avatar} 
                alt={post.author.name} 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <p className="font-medium">{post.author.name}</p>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {formatDate(post.publishedAt)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {post.readTime} min read
                  </span>
                </div>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleShare}>
            <Share className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      </div>

      {/* Featured image */}
      <div className="max-w-5xl mx-auto mb-12">
        <img 
          src={post.coverImage} 
          alt={post.title} 
          className="w-full h-auto rounded-xl object-cover"
        />
      </div>

      {/* Article content */}
      <div className="max-w-4xl mx-auto">
        <div 
          className="prose prose-lg max-w-none mb-12"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Post navigation */}
        <div className="border-t border-b py-6 my-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          {prevPost ? (
            <Link 
              to={`/blog/${prevPost.slug}`}
              className="flex items-center group"
            >
              <ChevronLeft className="h-5 w-5 mr-2 transition-transform group-hover:-translate-x-1" />
              <div>
                <p className="text-sm text-muted-foreground">Previous Article</p>
                <p className="font-medium group-hover:text-primary transition-colors">{prevPost.title}</p>
              </div>
            </Link>
          ) : <div />}

          {nextPost && (
            <Link 
              to={`/blog/${nextPost.slug}`}
              className="flex items-center justify-end text-right group ml-auto"
            >
              <div>
                <p className="text-sm text-muted-foreground">Next Article</p>
                <p className="font-medium group-hover:text-primary transition-colors">{nextPost.title}</p>
              </div>
              <ChevronRight className="h-5 w-5 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          )}
        </div>
      </div>

      {/* Related articles */}
      {relatedPosts.length > 0 && (
        <div className="max-w-6xl mx-auto mt-16">
          <h2 className="text-2xl font-bold mb-8">Related Articles</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {relatedPosts.map((relatedPost) => (
              <Card key={relatedPost.id} className="flex flex-col h-full overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={relatedPost.coverImage} 
                    alt={relatedPost.title} 
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
                <CardContent className="flex flex-col flex-grow p-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(relatedPost.publishedAt)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {relatedPost.readTime} min read
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 line-clamp-2">{relatedPost.title}</h3>
                  <p className="text-muted-foreground mb-4 line-clamp-2">{relatedPost.excerpt}</p>
                  <Link to={`/blog/${relatedPost.slug}`} className="mt-auto">
                    <Button variant="outline" size="sm" className="w-full">
                      Read Article
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};